/* ============================================
   AUTH CONTEXT - Global Authentication State
   Secure Session Management
   ============================================ */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storeAuth, getAuth, clearAuth, isSessionValid, generateToken } from '../utils/auth';

const AuthContext = createContext(null);

const ADMIN_CREDENTIALS = {
  username: process.env.REACT_APP_ADMIN_USERNAME || 'admin',
  password: process.env.REACT_APP_ADMIN_PASSWORD || 'muf2007@A',
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    if (auth && isSessionValid()) {
      setUser(auth.user);
    } else {
      clearAuth();
    }
    setLoading(false);
  }, []);

  const login = useCallback((username, password) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          username === ADMIN_CREDENTIALS.username &&
          password === ADMIN_CREDENTIALS.password
        ) {
          const token = generateToken(username);
          const userData = {
            id: 'usr_001',
            username,
            role: 'superadmin',
            avatar: null,
            displayName: 'Administrator',
            email: 'admin@wormatrix.fun',
            createdAt: new Date().toISOString(),
          };
          storeAuth(token, userData);
          setUser(userData);
          resolve({ success: true, user: userData });
        } else {
          reject(new Error('Invalid username or password'));
        }
      }, 800);
    });
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setUser(null);
    window.location.href = '/';
  }, []);

  const updateProfile = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return null;
      const next = { ...prev, ...updates, updatedAt: new Date().toISOString() };
      storeAuth(generateToken(next.username), next);
      return next;
    });
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'superadmin',
    login,
    logout,
    updateProfile,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
